<template>
  <div :class="['flex', 'flex-col', 'gap-3', { 'flex-grow': !props.noGrow }]">
    <div class="flex flex-row justify-between pr-2">
      <div class="flex flex-row items-center justify-between w-full">
        <div class="flex flex-row flex-wrap gap-2">
          <template v-for="filter in visibleActiveFilters" :key="filter.id">
            <FilterDropdownItem
              :filter="filter"
              :is-open="isFilterOpen(filter)"
              :selected-count="getFilterSelectedCount(filter)"
              :is-filter-applied="isFilterApplied(filter)"
              :selected-names="getSelectedNames(filter)"
              :title="getFilterTitle(filter)"
              :ondelete="handleDeleteFilter"
              :hide-title="
                showCustomDateFilter &&
                !props.showOperators &&
                (dateFilterTypes.includes(filter.id) || filter.type === 'date')
              "
              :locked="isFilterLocked(filter)"
              @toggle="toggleDropdown"
              @clear="clearAllSelections"
            >
              <CustomDateFilter
                v-if="
                  showCustomDateFilter &&
                  !props.showOperators &&
                  (dateFilterTypes.includes(filter.id) ||
                    filter.type === 'date')
                "
                :source="openDropdownId"
                :parent-format="dateTimeFormats[3]"
                :start-date="startDate"
                :end-date="endDate"
                :allow-future-dates="filter.type === 'date'"
                @update:customDateChange="handleDateChange"
                @goBack="handleBackButton"
              />
              <DurationFilterInput
                v-else-if="isSelectTimestampFilter(filter)"
                :ref="(el) => setFilterInputRef(filter.id, el)"
                :filter-id="filter.attribute_id || filter.id"
                :filter-name="filter.name"
                :field-type="'select_timestamp'"
                :current-value="getCurrentValue(filter)"
                @submit="(data) => handleFilterSubmit(data, filter)"
                @clear="(data) => handleFilterClear(data, filter)"
              >
                <template #delete-icon>
                  <ThemeImage
                    :icon="imageNames.DUSTBIN_GREY"
                    alt="delete_icon"
                    class="cursor-pointer size-4"
                    @click.stop="handleDeleteFilter(filter)"
                  />
                </template>
              </DurationFilterInput>
              <FilterInput
                v-else-if="shouldUseFilterInput(filter)"
                :ref="(el) => setFilterInputRef(filter.id, el)"
                :filter-id="filter.attribute_id || filter.id"
                :filter-name="filter.name"
                :field-type="getFieldTypeForFilter(filter)"
                :current-value="getCurrentValue(filter)"
                :options="filter.options"
                :show-operators="props.showOperators"
                @submit="(data) => handleFilterSubmit(data, filter)"
                @clear="(data) => handleFilterClear(data, filter)"
                :attribute="filter"
                :related-objects="filter.related_objects || []"
                :use-date-presets="shouldUseDatePresets(filter)"
                :date-preset-options="dynamicCRMDateFilterOptions"
                @search="
                  (data) =>
                    handleFilterSearch(data.filterId, data.searchQuery, filter)
                "
                @custom-date="handleCustomDate"
                :sort-selected-first="
                  props.sortSelectedFirst ||
                  filter.sortSelectedFirst ||
                  filter.showInitials
                "
              >
                <template #delete-icon>
                  <ThemeImage
                    :icon="imageNames.DUSTBIN_GREY"
                    alt="delete_icon"
                    class="cursor-pointer size-4"
                    @click.stop="handleDeleteFilter(filter)"
                  />
                </template>
              </FilterInput>
              <template v-else>
                <div class="flex items-center justify-between p-3 pt-1">
                  <span class="text-sm font-semibold text_color">{{
                    filter.name
                  }}</span>
                  <ThemeImage
                    :icon="imageNames.DUSTBIN_GREY"
                    alt="delete_icon"
                    class="cursor-pointer size-4"
                    @click.stop="handleDeleteFilter(filter)"
                  />
                </div>
                <FilterOptionsList
                  :filter-id="filter.id"
                  :filter-name="filter.name"
                  :options="getFilteredOptions(filter)"
                  :searchable="filter.searchable"
                  :show-initials="filter.showInitials"
                  :is-date="filter.type === 'date'"
                  :is-loading="filter.isLoading"
                  @select-option="(option) => handleOptionClick(option, filter)"
                  @search="
                    (filterId, query) =>
                      handleFilterSearch(filterId, query, filter)
                  "
                  @custom-date="handleCustomDate"
                />
              </template>
            </FilterDropdownItem>
          </template>

          <CustomFiltersDropdown
            v-if="!hideAddFilterButton"
            ref="dropdownRef"
            :filters-options="computedFiltersOptions"
            :dynamic-filter-options="computedDynamicFilterOptions"
            :is-dynamic-crm="true"
            :searchable="searchable"
            :button-text="buttonText"
            :is-loading="isLoadingFilters"
            :show-object-selector="showObjectSelector"
            :available-objects="availableObjects"
            :selected-object-id="selectedObjectId"
            :loading-objects="loadingObjects"
            :allow-id-filter="allowIdFilter"
            @select-option="onOptionClick"
            @search="handleSearch"
            @object-change="handleObjectChange"
            @close="handleCustomFiltersDropdownClose"
          />
        </div>

        <div class="flex items-center gap-2">
          <span
            v-if="
              !props.hideResetButton &&
              (visibleActiveFilters.length > 0 ||
                hasActiveSorts ||
                props.hasExternalActiveFilters)
            "
            @click="clearAllFiltersAndSorts"
            class="cursor-pointer text_color text-sm font-semibold dropdown_item !w-auto"
          >
            Reset
          </span>

          <ViewMoreOptionsDropdown
            v-if="
              !hideMoreOptions && (showExportOption || (isDynamicCRM && slug))
            "
            ref="moreOptionsDropdownRef"
            :slug="viewSlug"
            :has-no-results="hasNoResults"
            :show-export-option="showExportOption"
            :export-only="showExportOption && !isDynamicCRM && !slug"
            :current-columns="currentColumns"
            :frozen-columns="frozenColumns"
            :view-id="viewId"
            :access-options-by-tab="accessOptionsByTab"
            :entity-slug="slug"
            :current-view="currentView"
            :sharing-details="sharingDetails"
            :project-id="projectId"
            :service="service"
            :scope-entity-id="scopeEntityId"
            :hide-permissions-option="hidePermissionsOption"
            @columns-updated="handleColumnsUpdated"
            @frozen-columns-updated="handleFrozenColumnsUpdated"
            @open-export-modal="handleOpenExportModal"
            @view-created="handleViewCreated"
            @view-updated="handleViewUpdated"
          />
        </div>
      </div>
    </div>

    <BulkExportModal
      v-if="!hideMoreOptions"
      :source="'dynamic_crm'"
      :title="`Export ${currentView?.name || `All ${entityDisplayName}`}`"
      :description="`'${currentView?.name || `All ${entityDisplayName}`}' will be exported to your email`"
      :select-count="totalRecords || 0"
      :max-rows="source === entityTypes.CALL_LOG ? 50000 : 25000"
      v-model:visible="showExportModal"
      @confirm="handleExportConfirm"
    />
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  reactive,
  nextTick,
} from "vue";
import DurationFilterInput from "./DurationFilterInput.vue";
import { showDynamicToast } from "@/src/utils/utils.js";
import { formatISODate } from "@/src/utils/dateTimeUtils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { dateTimeFormats } from "@/src/constants/dateTimeConstants.js";
import { calculateDateRange } from "@/src/constants/filterConstants.js";

import {
  getFilterTypeFromDatatype,
  getEntityTypeFromSource,
  defaultFilterOptions,
  dateFilterTypes,
  defaultDateFilters,
  searchableFilterIds,
  standardObjectStatusFields,
  buildFilterFlags,
  mapFilterId,
  booleanOptions,
  isNumericField,
  isDateField,
  isBooleanField,
  isSelectField,
  isUserField,
  isTagsField,
  isSelectTimestampField,
  ATTRIBUTE_TYPES,
} from "@/src/constants/crmConstants.js";
import {
  operatorsWithoutValue,
  filterOperators,
} from "@/src/constants/analyticsFilterConstants.js";
import {
  toFilterType,
  isMultiSelect,
  loadFilterOptions,
  applyFilterFlags,
} from "@/src/utils/crmFilterUtils.js";
import { viewLayoutTypes } from "@/src/constants/viewConstants.js";
import { message } from "ant-design-vue";
import store from "@/src/store/index.js";
import TicketsService from "@/src/api/TicketsService.js";
import DashboardService from "@/src/api/DashboardService.js";
import CallService from "@/src/api/CallService.js";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { buildFiltersArray } from "@/src/utils/voiceAgentUtils.js";
import dayjs from "dayjs";
import { debounce } from "lodash";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomDateFilter from "@/src/components/CustomDateFilter.vue";
import FilterInput from "@/src/components/view_components/FilterInput.vue";
import FilterOptionsList from "@/src/components/view_components/FilterOptionsList.vue";
import FilterDropdownItem from "@/src/components/view_components/FilterDropdownItem.vue";
import BulkExportModal from "@/src/components/custom_table_components/BulkExportModal.vue";
import CustomFiltersDropdown from "@/src/components/view_components/CustomFiltersDropdown.vue";
import ViewMoreOptionsDropdown from "@/src/components/view_components/ViewMoreOptionsDropdown.vue";

const props = defineProps({
  source: { type: String, required: false },
  filtersOptions: { type: Array, default: () => [] },
  filterData: { type: Object, default: () => ({}) },
  sortData: { type: Object, default: () => ({}) },
  options: { type: Array, default: () => [] },
  buttonText: { type: String, default: "Filters" },
  icon: { type: String, default: imageNames.FILTER_GREY },
  searchable: { type: Boolean, default: true },
  searchPlaceholder: { type: String, default: "Search here" },
  showClearAll: { type: Boolean, default: true },
  showExportOption: { type: Boolean, default: false },
  showOperators: { type: Boolean, default: false },
  currentView: { type: Object, default: null },
  totalSize: { type: Number, default: 10 },
  isDynamicCRM: { type: Boolean, default: false },
  allowIdFilter: { type: Boolean, default: false }, //for sequential id
  displayPrefix: { type: String, default: "" }, //for sequential id
  slug: { type: String, default: "" },
  viewSlug: { type: String, default: "" },
  currentColumns: { type: Array, default: () => [] },
  frozenColumns: { type: Array, default: () => [] },
  viewId: { type: [String, Number], default: null },
  projectId: { type: [String, Number], default: null },
  scopeEntityId: { type: [String, Number], default: null },
  accessOptionsByTab: {
    type: Object,
    default: () => ({ Users: [], Teams: [] }),
  },
  sharingDetails: { type: Object, default: null },
  hideMoreOptions: { type: Boolean, default: false },
  showObjectSelector: { type: Boolean, default: false },
  filteredObjects: { type: Array, default: null },
  saveOnDropdownClose: { type: Boolean, default: false },
  rawFilterKeys: { type: Array, default: () => [] },
  searchQuery: { type: String, default: "" },
  sortSelectedFirst: { type: Boolean, default: false },
  excludeFieldTypes: { type: Array, default: () => [] },
  service: { type: String, default: null },
  hasExternalActiveFilters: { type: Boolean, default: false },
  noGrow: { type: Boolean, default: false },
  hideResetButton: { type: Boolean, default: false },
  hideAddFilterButton: { type: Boolean, default: false },
  hiddenFilterKeys: { type: Array, default: () => [] },
  lockedFilterKeys: { type: Array, default: () => [] },
  hidePermissionsOption: { type: Boolean, default: false },
});

const emit = defineEmits([
  "select-option",
  "search",
  "filter-changed",
  "clear-all-filters",
  "clear-all-sorts",
  "filter-search",
  "columns-updated",
  "frozen-columns-updated",
  "view-created",
  "view-updated",
  "dropdown-closed",
  "object-change",
  "active-filters-count-changed",
]);

const dropdownRef = ref(null);
const activeFilters = ref([]);
const openDropdownId = ref(null);
const manuallyAddedFilterIds = ref(new Set());
const startDate = ref(dayjs());
const endDate = ref(dayjs());
const showCustomDateFilter = ref(false);

const isLoadingFilters = ref(false);
const dynamicFilterOptions = ref([]);
const filterInputRefs = ref({});

const selectedObjectId = ref(null);
const availableObjects = computed(() => {
  if (props.filteredObjects && props.filteredObjects.length > 0) {
    return props.filteredObjects;
  }
  return store.getters["objects/objects"] || [];
});
const loadingObjects = computed(() => store.getters["objects/loadingObjects"]);

const showExportModal = ref(false);
const totalRecords = computed(() => props.totalSize || 0);

const hasActiveSorts = computed(() => {
  return props.sortData && Object.keys(props.sortData).length > 0;
});

const isExportOnly = computed(() => {
  return props.showExportOption && !props.isDynamicCRM && !props.slug;
});

const hasNoResults = computed(() => {
  return props.totalSize === 0;
});

const entityDisplayName = computed(() => {
  return props.slug || props.source || "entity";
});

const filterGroupedAttribute = (filters, groupByConfig) => {
  if (!groupByConfig || !filters?.length) {
    return filters || [];
  }

  if (props.currentView?.view_type !== viewLayoutTypes.BOARD) {
    return filters || [];
  }

  const { attribute_slug, attribute_id } = groupByConfig;
  const isPipelineGroupBy = attribute_slug === "pipeline_stage_id";

  return filters.filter((f) => {
    if (isPipelineGroupBy) {
      return f.id !== "pipeline_id" && f.id !== "deal_stage";
    }
    return f.id !== attribute_slug && f.id !== attribute_id;
  });
};

const computedFiltersOptions = computed(() => {
  let filtered = filterGroupedAttribute(
    props.filtersOptions,
    props.currentView?.group_by_config,
  );

  // Filter out locked filter keys from dropdown options
  if (props.lockedFilterKeys?.length > 0) {
    const lockedSet = new Set(props.lockedFilterKeys);
    filtered = filtered.filter(
      (f) =>
        !lockedSet.has(f.id) &&
        !lockedSet.has(f.slug) &&
        !lockedSet.has(f.attribute_id) &&
        !lockedSet.has(f.name?.toLowerCase()?.replace(/\s+/g, "_")),
    );
  }

  return filtered;
});

const computedDynamicFilterOptions = computed(() => {
  let filtered = filterGroupedAttribute(
    dynamicFilterOptions.value,
    props.currentView?.group_by_config,
  );

  // Filter out dynamic options that match static filters by id or attribute_id
  if (props.filtersOptions?.length > 0 && filtered?.length > 0) {
    const staticFilterIds = new Set(props.filtersOptions.map((f) => f.id));
    const staticFilterAttributeIds = new Set(
      props.filtersOptions.map((f) => f.attribute_id).filter(Boolean),
    );

    filtered = filtered.filter((dynamicFilter) => {
      // Check both dynamicFilter.id and dynamicFilter.attribute_id against static filter ids
      const matchesStaticId =
        staticFilterIds.has(dynamicFilter.id) ||
        staticFilterIds.has(dynamicFilter.attribute_id);
      const matchesStaticAttributeId =
        staticFilterAttributeIds.has(dynamicFilter.id) ||
        staticFilterAttributeIds.has(dynamicFilter.attribute_id);
      return !(matchesStaticId || matchesStaticAttributeId);
    });
  }

  if (props.excludeFieldTypes.length > 0 && filtered?.length > 0) {
    const excludeSet = new Set(
      props.excludeFieldTypes.map((t) => t.toLowerCase()),
    );
    filtered = filtered.filter(
      (f) => !excludeSet.has((f.type || "").toLowerCase()),
    );
  }

  // Filter out locked filter keys from dropdown options
  if (props.lockedFilterKeys?.length > 0 && filtered?.length > 0) {
    const lockedSet = new Set(props.lockedFilterKeys);
    filtered = filtered.filter(
      (f) =>
        !lockedSet.has(f.id) &&
        !lockedSet.has(f.slug) &&
        !lockedSet.has(f.attribute_id) &&
        !lockedSet.has(f.name?.toLowerCase()?.replace(/\s+/g, "_")),
    );
  }

  return filtered;
});

const visibleActiveFilters = computed(() => {
  const hiddenSet = new Set(props.hiddenFilterKeys);
  const filters =
    hiddenSet.size > 0
      ? activeFilters.value.filter(
          (f) =>
            !hiddenSet.has(f.id) &&
            !hiddenSet.has(f.slug) &&
            !hiddenSet.has(f.attribute_id) &&
            !hiddenSet.has(f.name?.toLowerCase()?.replace(/\s+/g, "_")),
        )
      : activeFilters.value;
  return filterGroupedAttribute(filters, props.currentView?.group_by_config);
});

watch(
  () => visibleActiveFilters.value.length,
  (count) => emit("active-filters-count-changed", count),
  { immediate: true },
);

// computedDynamicFilterOptions already excludes dynamic filters that overlap
// with static filters, so we can simply concatenate the two lists.
const allAvailableFilterOptions = computed(() => {
  return [
    ...computedFiltersOptions.value,
    ...computedDynamicFilterOptions.value,
  ];
});

const onOptionClick = async (option) => {
  const optionAttributeId = option.attribute_id || option.id;
  const existingFilter = activeFilters.value.find(
    (filter) =>
      filter.id === option.id ||
      filter.attribute_id === option.id ||
      filter.id === optionAttributeId ||
      filter.attribute_id === optionAttributeId,
  );

  if (!existingFilter) {
    const fieldType = String(
      option.type || option.datatype || "",
    ).toLowerCase();

    const hasStaticOptions = option.options && option.options.length > 0;
    const hasFetchOptions = typeof option.fetchOptions === "function";

    const newFilter = reactive({
      id: option.id,
      attribute_id: option.attribute_id || option.id,
      slug: option.slug, // Include slug for value lookup (handles renamed fields)
      name: option.name,
      type: option.type,
      datatype: option.datatype,
      multiSelect:
        option.multiSelect !== undefined
          ? option.multiSelect
          : isMultiSelect(option),
      searchable:
        option.searchable === true ||
        option.type === "text" ||
        option.type === "user" ||
        option.type === "relation_record" ||
        searchableFilterIds.includes(option.id),
      showInitials: option.type === "user",
      options: option.options || [],
      originalOptions: hasStaticOptions ? [...option.options] : [],
      related_objects: option.related_objects || [],
      isLoading: !hasStaticOptions && !hasFetchOptions,
      fetchOptions: option.fetchOptions,
      searchOptions: option.searchOptions,
      dependsOn: option.dependsOn,
      sortSelectedFirst: option.sortSelectedFirst || false,
      // Preserve the display prefix so FilterInput's a-input can render
      // it as a visual `#prefix` slot (e.g. "INV-" on the invoice id filter).
      ...(option.displayPrefix ? { displayPrefix: option.displayPrefix } : {}),
    });

    const flags = buildFilterFlags(fieldType, newFilter);
    Object.assign(newFilter, flags);

    activeFilters.value.push(newFilter);
    manuallyAddedFilterIds.value.add(newFilter.id);

    dropdownRef.value?.closeDropdown?.();

    setTimeout(() => {
      openDropdownId.value = newFilter.id;

      if (!hasStaticOptions) {
        nextTick(async () => {
          if (hasFetchOptions) {
            try {
              newFilter.isLoading = true;
              const options = await option.fetchOptions(
                props.filterData,
                store,
              );
              newFilter.options = options || [];
              newFilter.originalOptions = options || [];
              newFilter.isLoading = false;
            } catch (error) {
              console.error("Error fetching filter options:", error);
              newFilter.isLoading = false;
            }
          } else {
            fetchDynamicFilterData(newFilter);
          }
        });
      }
    }, 150);
  } else {
    dropdownRef.value?.closeDropdown?.();
    setTimeout(() => {
      openDropdownId.value = existingFilter.id;
    }, 150);
  }

  emit("select-option", option);
};

const isFilterOpen = (filter) => {
  return openDropdownId.value === filter.id;
};

const isFilterLocked = (filter) => {
  if (!props.lockedFilterKeys?.length) return false;
  const lockedSet = new Set(props.lockedFilterKeys);
  return (
    lockedSet.has(filter.id) ||
    lockedSet.has(filter.slug) ||
    lockedSet.has(filter.attribute_id) ||
    lockedSet.has(filter.name?.toLowerCase()?.replace(/\s+/g, "_"))
  );
};

const getFilterSelectedCount = (filter) => {
  const selectedIds = getFilterValue(filter);

  if (selectedIds && typeof selectedIds === "object" && selectedIds.operator) {
    if (Array.isArray(selectedIds.value)) {
      return selectedIds.value.length;
    }
    if (operatorsWithoutValue.includes(selectedIds.operator)) {
      return 1;
    }
    return selectedIds.value !== null && selectedIds.value !== undefined
      ? 1
      : 0;
  }

  return Array.isArray(selectedIds)
    ? selectedIds.length
    : selectedIds !== null && selectedIds !== undefined
      ? 1
      : 0;
};

const getOptionLabel = (value, options, isBooleanField = false) => {
  if (value === -1 || value === "-1") {
    return "Only Me";
  }

  if (isBooleanField) {
    let boolVal = value;
    if (typeof value === "string") {
      boolVal = value === "true";
    }
    const option = booleanOptions.find((opt) => opt.value === boolVal);
    return option ? option.label : String(value);
  }

  // Handle case where value itself is an object with name/label (like tags)
  if (value && typeof value === "object") {
    if (value.name) return value.name;
    if (value.label) return value.label;
    if (value.id) {
      // Try to find the option by id
      const option = options?.find((opt) => opt.id === value.id);
      return option ? option.name || option.label : String(value.id);
    }
    return String(value);
  }

  const option = options?.find(
    (opt) => opt.id === value || opt.value === value,
  );
  return option ? option.name || option.label : value;
};

const extractFilterValues = (selectedIds) => {
  if (!selectedIds) return null;

  if (typeof selectedIds === "object" && selectedIds.operator) {
    return selectedIds.value;
  }

  return selectedIds;
};

const getSelectedNames = (filter) => {
  const selectedIds = getFilterValue(filter);
  if (selectedIds === null || selectedIds === undefined) return [];

  if (filter.isSelectTimestampInput || isSelectTimestampFilter(filter)) {
    if (
      selectedIds &&
      typeof selectedIds === "object" &&
      selectedIds.operator
    ) {
      const operatorLabel =
        filterOperators.find((op) => op.id === selectedIds.operator)?.label ||
        selectedIds.operator;

      if (
        selectedIds.operator === "is_empty" ||
        selectedIds.operator === "is_not_empty"
      ) {
        return [operatorLabel];
      }

      if (
        selectedIds.operator === "between" ||
        selectedIds.operator === "not_between"
      ) {
        if (
          selectedIds.values &&
          Array.isArray(selectedIds.values) &&
          selectedIds.values.length >= 2
        ) {
          const val1 = selectedIds.values[0];
          const val2 = selectedIds.values[1];
          if (val1 && val2 && val1.duration != null && val2.duration != null) {
            return [
              `${operatorLabel} ${val1.duration} ${val1.unit} and ${val2.duration} ${val2.unit}`,
            ];
          }
        }
        return [];
      }

      if (selectedIds.value && typeof selectedIds.value === "object") {
        if (selectedIds.value.duration != null) {
          return [
            `${operatorLabel} ${selectedIds.value.duration} ${selectedIds.value.unit}`,
          ];
        }
      }

      return [];
    }
    return [];
  }

  const isRecordType =
    filter.datatype?.toLowerCase() === ATTRIBUTE_TYPES.RECORD ||
    filter.type?.toLowerCase() === ATTRIBUTE_TYPES.RECORD;
  if (isRecordType) {
    let recordValues = [];

    if (
      selectedIds &&
      typeof selectedIds === "object" &&
      selectedIds.operator
    ) {
      recordValues = Array.isArray(selectedIds.value) ? selectedIds.value : [];
    } else if (Array.isArray(selectedIds)) {
      recordValues = selectedIds;
    }

    return recordValues
      .filter((record) => record && typeof record === "object")
      .map(
        (record) =>
          record.record_display_name ||
          record.entity_name ||
          record.name ||
          record.record_id ||
          record.entity_id ||
          "Unknown",
      );
  }

  // Handle date filters with preset values (today, yesterday, last_7_days, etc.)
  const isDateFilter =
    filter.type === "date" ||
    filter.datatype === "Date" ||
    filter.datatype === "date" ||
    dateFilterTypes.includes(filter.id);

  if (isDateFilter && selectedIds) {
    // Handle case where selectedIds is an object with operator
    if (typeof selectedIds === "object" && selectedIds.operator) {
      const presetValue = selectedIds.value;

      // Check if it's a between operator with date range array
      if (
        (selectedIds.operator === "between" ||
          selectedIds.operator === "not_between") &&
        Array.isArray(presetValue) &&
        presetValue.length >= 2
      ) {
        return [`${presetValue[0]} to ${presetValue[1]}`];
      }

      // Check if the value is a date preset string
      const datePreset = dynamicCRMDateFilterOptions.find(
        (opt) => opt.id === presetValue,
      );
      if (datePreset) {
        return [datePreset.name];
      }
      // If it's a custom date value (actual date string), return it
      if (presetValue && typeof presetValue === "string") {
        return [presetValue];
      }
    }
    // Handle case where selectedIds is an object with option (e.g., from view service: { option: "last_30_days", start_date: "...", end_date: "..." })
    else if (typeof selectedIds === "object" && selectedIds.option) {
      if (
        selectedIds.option === "custom" &&
        selectedIds.start_date &&
        selectedIds.end_date
      ) {
        return [`${selectedIds.start_date} to ${selectedIds.end_date}`];
      }
      const datePreset = dynamicCRMDateFilterOptions.find(
        (opt) => opt.id === selectedIds.option,
      );
      if (datePreset) {
        return [datePreset.name];
      }
      return [selectedIds.option];
    }
    // Handle case where selectedIds is a simple string (e.g., "last_3_days" from saved view)
    else if (typeof selectedIds === "string") {
      const datePreset = dynamicCRMDateFilterOptions.find(
        (opt) => opt.id === selectedIds,
      );
      if (datePreset) {
        return [datePreset.name];
      }
      // Return the raw value if no preset match found
      return [selectedIds];
    }
  }

  if (filter.isTextInput) {
    if (
      selectedIds &&
      typeof selectedIds === "object" &&
      selectedIds.operator
    ) {
      if (
        selectedIds.value &&
        typeof selectedIds.value === "object" &&
        selectedIds.value.start &&
        selectedIds.value.end
      ) {
        return [`${selectedIds.value.start} to ${selectedIds.value.end}`];
      }
      if (selectedIds.value) {
        return [selectedIds.value.toString()];
      }
      if (selectedIds.value1 && selectedIds.value2) {
        return [`${selectedIds.value1} to ${selectedIds.value2}`];
      }
      return [];
    }
    return [selectedIds.toString()];
  }

  const isBooleanField = filter.datatype?.toLowerCase() === "boolean";

  if (
    selectedIds &&
    typeof selectedIds === "object" &&
    "option" in selectedIds
  ) {
    if (
      selectedIds.option === "custom" &&
      selectedIds.start_date &&
      selectedIds.end_date
    ) {
      return [`${selectedIds.start_date} to ${selectedIds.end_date}`];
    }
    const optionName = filter.options?.find(
      (opt) => opt.id === selectedIds.option,
    )?.name;
    return optionName ? [optionName] : [selectedIds.option];
  }

  const values = extractFilterValues(selectedIds);
  if (values === null || values === undefined) return [];

  const valuesArray = Array.isArray(values) ? values : [values];
  // Use originalOptions (full unfiltered list) for name lookup when available,
  // since filter.options may be a search-filtered subset missing some selected values
  const lookupOptions =
    filter.originalOptions && filter.originalOptions.length > 0
      ? filter.originalOptions
      : filter.options;
  return valuesArray.map((val) =>
    getOptionLabel(val, lookupOptions, isBooleanField),
  );
};

const isFilterApplied = (filter) => {
  const selectedIds = getFilterValue(filter);

  if (selectedIds && typeof selectedIds === "object" && selectedIds.operator) {
    if (operatorsWithoutValue.includes(selectedIds.operator)) {
      return true;
    }

    if (selectedIds.values && Array.isArray(selectedIds.values)) {
      return selectedIds.values.length > 0;
    }

    if (Array.isArray(selectedIds.value)) {
      return selectedIds.value.length > 0;
    }
    return selectedIds.value !== null && selectedIds.value !== undefined;
  }

  return Array.isArray(selectedIds)
    ? selectedIds.length > 0
    : selectedIds !== null && selectedIds !== undefined;
};

const toggleDropdown = (filterId) => {
  openDropdownId.value = openDropdownId.value === filterId ? null : filterId;
};

const setFilterInputRef = (filterId, el) => {
  if (el) {
    filterInputRefs.value[filterId] = el;
  } else {
    delete filterInputRefs.value[filterId];
  }
};

const clearAllSelections = (filter) => {
  emit("filter-changed", {
    filter: filter.attribute_id || filter.id,
    updated_values: null,
  });
};

const resetInternalState = () => {
  activeFilters.value = [];
  manuallyAddedFilterIds.value.clear();
};

const clearAllFilters = () => {
  resetInternalState();
  emit("clear-all-filters", {});
};

const clearAllFiltersAndSorts = () => {
  // Preserve locked filters
  const lockedFiltersData = {};
  if (props.lockedFilterKeys?.length > 0) {
    const lockedSet = new Set(props.lockedFilterKeys);

    // First, preserve locked filters from activeFilters
    for (const filter of activeFilters.value) {
      const filterKey = filter.attribute_id || filter.id;
      const isLocked =
        lockedSet.has(filter.id) ||
        lockedSet.has(filter.slug) ||
        lockedSet.has(filter.attribute_id) ||
        lockedSet.has(filter.name?.toLowerCase()?.replace(/\s+/g, "_"));

      if (isLocked) {
        const filterValue = getFilterValue(filter);
        if (filterValue !== null && filterValue !== undefined) {
          lockedFiltersData[filterKey] = filterValue;
        }
      }
    }

    // Also preserve locked filters from filterData that might not be in activeFilters
    // (e.g., if the filter definition wasn't found during initialization)
    for (const lockedKey of props.lockedFilterKeys) {
      if (!(lockedKey in lockedFiltersData)) {
        const filterValue = props.filterData[lockedKey];
        if (filterValue !== null && filterValue !== undefined) {
          lockedFiltersData[lockedKey] = filterValue;
        }
      }
    }

    // Keep locked filters in activeFilters
    activeFilters.value = activeFilters.value.filter(
      (filter) =>
        lockedSet.has(filter.id) ||
        lockedSet.has(filter.slug) ||
        lockedSet.has(filter.attribute_id) ||
        lockedSet.has(filter.name?.toLowerCase()?.replace(/\s+/g, "_")),
    );
    manuallyAddedFilterIds.value.clear();
  } else {
    resetInternalState();
  }

  emit("clear-all-filters", lockedFiltersData);
  emit("clear-all-sorts", {});
};

const getFilterTitle = (filter) => {
  return filter.name;
};

const findObjectWithFilterProperties = (
  objects,
  filterData,
  rawFilterKeys = [],
) => {
  const keysToMatch =
    rawFilterKeys.length > 0 ? rawFilterKeys : Object.keys(filterData || {});

  if (keysToMatch.length === 0) {
    return null;
  }

  // First pass: try to find object with all matching properties
  for (const obj of objects) {
    if (!obj.properties) continue;

    const matchingPropertiesCount = keysToMatch.filter((filterKey) => {
      return obj.properties.some(
        (prop) => prop.slug === filterKey || prop.id === filterKey,
      );
    }).length;

    if (matchingPropertiesCount === keysToMatch.length) {
      return obj;
    }
  }

  // Second pass: find object with any matching property
  for (const obj of objects) {
    if (!obj.properties) continue;

    const hasAnyMatchingProperty = keysToMatch.some((filterKey) => {
      return obj.properties.some(
        (prop) => prop.slug === filterKey || prop.id === filterKey,
      );
    });

    if (hasAnyMatchingProperty) {
      return obj;
    }
  }

  return null;
};

const isSelectTimestampFilter = (filter) => {
  const datatype = String(filter.datatype || filter.type || "").toLowerCase();
  return isSelectTimestampField(datatype) || filter.isSelectTimestampInput;
};

const shouldUseFilterInput = (filter) => {
  // Don't use FilterInput for select_timestamp - use DurationFilterInput instead
  if (isSelectTimestampFilter(filter)) {
    return false;
  }
  // Use FilterInput for date filters in DynamicCRM with date presets
  if (shouldUseDatePresets(filter)) {
    return true;
  }
  const isBooleanByDatatype = filter.datatype?.toLowerCase() === "boolean";
  const isRecordType =
    filter.datatype?.toLowerCase() === ATTRIBUTE_TYPES.RECORD ||
    filter.type?.toLowerCase() === ATTRIBUTE_TYPES.RECORD;
  return (
    filter.isTextInput ||
    filter.isBooleanInput ||
    isBooleanByDatatype ||
    filter.isSelectInput ||
    filter.isUserInput ||
    filter.isMultiSelectInput ||
    isRecordType
  );
};

const shouldUseDatePresets = (filter) => {
  if (!props.isDynamicCRM) return false;
  return (
    filter.type === "date" ||
    filter.datatype === "Date" ||
    filter.datatype === "date" ||
    dateFilterTypes.includes(filter.id)
  );
};

const getFilterValue = (filter) => {
  // Check multiple possible keys: attribute_id, id, and slug (for renamed fields)
  return (
    props.filterData[filter.attribute_id] ??
    props.filterData[filter.id] ??
    props.filterData[filter.slug]
  );
};

const getCurrentValue = (filter) => {
  const filterValue = getFilterValue(filter);

  if (filterValue && typeof filterValue === "object" && filterValue.operator) {
    return filterValue;
  }

  const isBooleanByDatatype = filter.datatype?.toLowerCase() === "boolean";
  if (
    (filter.isBooleanInput || isBooleanByDatatype) &&
    typeof filterValue === "boolean"
  ) {
    return filterValue;
  }

  if (
    (filter.isSelectInput ||
      filter.isUserInput ||
      filter.isMultiSelectInput ||
      isBooleanByDatatype) &&
    Array.isArray(filterValue)
  ) {
    return filterValue;
  }

  return filterValue || (filter.isTextInput ? "" : null);
};

const getFieldTypeForFilter = (filter) => {
  const datatype = String(filter.datatype || filter.type || "").toLowerCase();

  if (isSelectTimestampField(datatype)) {
    return "select_timestamp";
  }
  if (datatype === ATTRIBUTE_TYPES.RECORD) {
    return ATTRIBUTE_TYPES.RECORD;
  }
  if (isNumericField(datatype)) {
    return datatype;
  }
  if (isBooleanField(datatype)) {
    return "boolean";
  }
  if (isDateField(datatype)) {
    return datatype === "date" ? "date" : "datetime";
  }
  if (isSelectField(datatype)) {
    return "select";
  }
  if (isUserField(datatype)) {
    return "user";
  }
  if (isTagsField(datatype) || filter.multiSelect) {
    return "multi_select";
  }
  return "text";
};

const handleDeleteFilter = (filter) => {
  const index = activeFilters.value.findIndex((f) => f.id === filter.id);
  if (index > -1) {
    activeFilters.value.splice(index, 1);
  }

  manuallyAddedFilterIds.value.delete(filter.id);

  emit("filter-changed", {
    filter: filter.attribute_id || filter.id,
    updated_values: null,
  });

  const dependentActiveFilters = activeFilters.value.filter(
    (f) => f.dependsOn && f.dependsOn.includes(filter.id) && f.fetchOptions,
  );
  if (dependentActiveFilters.length > 0) {
    const updatedFilterData = { ...props.filterData };
    delete updatedFilterData[filter.attribute_id || filter.id];
    dependentActiveFilters.forEach(async (dependent) => {
      try {
        dependent.isLoading = true;
        const options = await dependent.fetchOptions(updatedFilterData, store);
        dependent.options = options || [];
        dependent.originalOptions = options || [];
        dependent.isLoading = false;
      } catch (error) {
        console.error(
          `Error refreshing dependent filter ${dependent.id}:`,
          error,
        );
        dependent.isLoading = false;
      }
    });
  }

  if (openDropdownId.value === filter.id) {
    openDropdownId.value = null;
  }
};

const getFilteredOptions = (filter) => {
  if (!filter || !filter.options) return [];

  return filter.options
    .filter((option) => option.id !== "custom")
    .map((option) => {
      const filterValue = getFilterValue(filter);
      let isSelected = false;

      if (
        filterValue &&
        typeof filterValue === "object" &&
        filterValue.operator
      ) {
        if (Array.isArray(filterValue.value)) {
          isSelected = filterValue.value.includes(option.id);
        } else {
          isSelected = filterValue.value === option.id;
        }
      } else {
        if (Array.isArray(filterValue)) {
          isSelected = filterValue.includes(option.id);
        } else if (
          filterValue &&
          typeof filterValue === "object" &&
          filterValue.option
        ) {
          // Date filter object from view service: { option: "last_30_days", start_date, end_date }
          isSelected = filterValue.option === option.id;
        } else {
          isSelected = filterValue === option.id;
        }
      }

      return {
        ...option,
        selected: isSelected,
      };
    });
};

const handleOptionClick = (option, filter) => {
  if (!filter || option.id == null) return;

  selectOption(option, filter);
};

const selectOption = (option, filter) => {
  if (!filter || option.id == null) return;

  let selectedIds = getFilterValue(filter);

  const isDateFilter =
    filter.type === "date" ||
    filter.datatype === "Date" ||
    filter.datatype === "date" ||
    dateFilterTypes.includes(filter.id);

  if (isDateFilter) {
    if (option.id === (selectedIds?.option || selectedIds)) {
      selectedIds = null;
    } else {
      const dateRange = calculateDateRange(option.id);
      selectedIds = {
        option: option.id,
        start_date: dateRange.start,
        end_date: dateRange.end,
      };
    }
  } else if (
    filter.multiSelect ||
    filter.type === "tags" ||
    filter.datatype === "tags"
  ) {
    const isNewFormat =
      selectedIds && typeof selectedIds === "object" && selectedIds.operator;

    if (isNewFormat) {
      const currentValues = Array.isArray(selectedIds.value)
        ? selectedIds.value
        : [];
      const index = currentValues.indexOf(option.id);

      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(option.id);
      }

      if (currentValues.length === 0) {
        selectedIds = null;
      } else {
        selectedIds = {
          operator: selectedIds.operator || "contains_any",
          value: currentValues,
        };
      }
    } else {
      if (!Array.isArray(selectedIds)) {
        selectedIds =
          selectedIds !== null && selectedIds !== undefined
            ? [selectedIds]
            : [];
      }

      const index = selectedIds.indexOf(option.id);
      if (index > -1) {
        selectedIds.splice(index, 1);
      } else {
        selectedIds.push(option.id);
      }

      if (selectedIds.length === 0) {
        selectedIds = null;
      } else {
        selectedIds = {
          operator: "contains_any",
          value: selectedIds,
        };
      }
    }
  } else {
    selectedIds = option.id === selectedIds ? null : option.id;
  }

  option.selected = !option.selected;

  const dependentFilters = allAvailableFilterOptions.value.filter(
    (opt) => opt.dependsOn && opt.dependsOn.includes(filter.id),
  );

  if (dependentFilters.length > 0) {
    dependentFilters.forEach((dependent) => {
      const dependentIndex = activeFilters.value.findIndex(
        (f) => f.id === dependent.id,
      );
      if (dependentIndex > -1) {
        activeFilters.value.splice(dependentIndex, 1);
        emit("filter-changed", {
          filter: dependent.attribute_id || dependent.id,
          updated_values: null,
        });
      }
    });
  }

  emit("filter-changed", {
    filter: filter.attribute_id || filter.id,
    updated_values: selectedIds,
  });
};

const handleOutsideClick = (event) => {
  const path = event.composedPath ? event.composedPath() : [];

  const filterClasses = [
    "filter-dropdown",
    "dropdown_list",
    "dropdown_item",
    "dropdown",
    "ant-picker-dropdown",
    "ant-picker",
    "ant-select-dropdown",
    "ant-input",
    "ant-select",
    "filter-input",
    "filter-options-list",
    "custom-filters-dropdown",
  ];

  const isInsideFilter = path.some((el) => {
    if (!(el instanceof Element)) return false;

    const matchedClass = filterClasses.find((cls) =>
      el.classList?.contains(cls),
    );
    if (matchedClass) {
      return true;
    }

    if (
      el.id === "custom_date_filter" ||
      el.hasAttribute?.("data-filter-dropdown-id")
    ) {
      return true;
    }

    return false;
  });

  if (!isInsideFilter) {
    openDropdownId.value = null;
  }
};

const handleCustomDate = (filterId) => {
  // Don't change openDropdownId - keep the current filter dropdown open
  // The filterId passed might be attribute_id which doesn't match filter.id

  const filter =
    activeFilters.value.find(
      (f) => f.id === filterId || f.attribute_id === filterId,
    ) ||
    allAvailableFilterOptions.value.find(
      (f) => f.id === filterId || f.attribute_id === filterId,
    );

  const dateFilter = filter
    ? getFilterValue(filter)
    : props.filterData[filterId];
  if (dateFilter && dateFilter.start_date && dateFilter.end_date) {
    startDate.value = dayjs(dateFilter.start_date);
    endDate.value = dayjs(dateFilter.end_date);
  } else {
    startDate.value = dayjs().subtract(30, "day");
    endDate.value = dayjs();
  }
  showCustomDateFilter.value = true;
};

const handleBackButton = (data) => {
  showCustomDateFilter.value = false;
  openDropdownId.value = data.source;
};

const handleDateChange = (data) => {
  startDate.value = dayjs(data.startDate);
  endDate.value = dayjs(data.endDate);

  const formattedStartDate = formatISODate(startDate.value, dateTimeFormats[3]);
  const formattedEndDate = formatISODate(endDate.value, dateTimeFormats[3]);
  const dateSelection = {
    option: "custom",
    start_date: formattedStartDate,
    end_date: formattedEndDate,
  };

  const filter =
    activeFilters.value.find((f) => f.id === data.source) ||
    allAvailableFilterOptions.value.find((f) => f.id === data.source);

  emit("filter-changed", {
    filter: filter ? filter.attribute_id || filter.id : data.source,
    updated_values: dateSelection,
  });
};

const handleFilterSubmit = (data, filter) => {
  if (!filter) return;

  const { filterId, value } = data;

  // Filter out empty strings from array values
  let cleanedValue = value;
  if (Array.isArray(value)) {
    cleanedValue = value.filter(
      (v) => v !== null && v !== undefined && v !== "",
    );
    if (cleanedValue.length === 0) cleanedValue = null;
  } else if (value && typeof value === "object" && Array.isArray(value.value)) {
    const filtered = value.value.filter(
      (v) => v !== null && v !== undefined && v !== "",
    );
    cleanedValue = filtered.length > 0 ? { ...value, value: filtered } : null;
  }

  emit("filter-changed", {
    filter: filterId,
    updated_values: cleanedValue,
  });

  // Don't close dropdown for "between" operator on date filters - user may still be selecting dates
  const isBetweenOperator =
    value &&
    typeof value === "object" &&
    (value.operator === "between" || value.operator === "not_between");

  if (!isBetweenOperator) {
    openDropdownId.value = null;
  }
};

const handleFilterClear = (data, filter) => {
  if (!filter) return;

  const { filterId } = data;

  emit("filter-changed", {
    filter: filterId,
    updated_values: null,
  });

  openDropdownId.value = null;
};

const closeDropdown = () => {
  showCustomDateFilter.value = false;
  startDate.value = dayjs();
  endDate.value = dayjs();
};

const handleColumnsUpdated = (updatedColumns) => {
  emit("columns-updated", updatedColumns);
};

const handleFrozenColumnsUpdated = (updatedFrozenColumns) => {
  emit("frozen-columns-updated", updatedFrozenColumns);
};

const handleSearch = (searchValue) => {
  emit("search", searchValue);
};

const handleObjectChange = async (objectId) => {
  try {
    const objectChanged = selectedObjectId.value !== objectId;

    selectedObjectId.value = objectId;

    if (objectChanged) {
      activeFilters.value = [];
    }

    const selectedObject = availableObjects.value.find(
      (obj) => obj.id === objectId,
    );
    if (!selectedObject || !selectedObject.properties) {
      return;
    }

    const mappedProperties = selectedObject.properties.map((prop) => {
      const propType = toFilterType(prop.type);

      let normalizedOptions = [];
      if (prop.type === "boolean") {
        normalizedOptions = booleanOptions;
      } else {
        normalizedOptions = (prop.options || []).map((opt) => {
          if (typeof opt === "string") {
            return { id: opt, name: opt };
          }
          return {
            id: opt?.id ?? opt,
            name: opt?.name ?? opt?.id ?? opt,
          };
        });
      }

      const filterBase = {
        id: prop.slug || prop.id,
        attribute_id: prop.id,
        name: prop.name || prop.label,
        type: propType,
        datatype: prop.type,
        options: normalizedOptions,
        originalOptions: normalizedOptions,
        is_multi_value: prop.is_multi_value || false,
        searchable: propType === "select" && normalizedOptions.length > 0,
        isLoading: false,
        // Decorate the numeric id filter with the schema's display prefix.
        ...(prop.slug === "id" && props.displayPrefix
          ? { displayPrefix: props.displayPrefix }
          : {}),
      };

      // Apply filter flags using our utility function for unified FilterInput
      const flags = applyFilterFlags(propType, filterBase);
      return { ...filterBase, ...flags };
    });

    dynamicFilterOptions.value = mappedProperties;

    // Only emit object-change when object actually changed (not on re-mount)
    if (objectChanged) {
      emit("object-change", objectId);
    }
  } catch (error) {
    console.error("Error handling object change:", error);
  }
};

const handleCustomFiltersDropdownClose = () => {
  if (props.saveOnDropdownClose) {
    emit("dropdown-closed");
  }
};

watch(openDropdownId, (newValue, oldValue) => {
  // Only close custom date filter when dropdown is being closed (newValue is null)
  // Don't close it when switching between filters or opening a new filter
  if (newValue === null) {
    closeDropdown();
  }

  if (oldValue !== null && newValue === null) {
    const filterInputRef = filterInputRefs.value[oldValue];
    const filter = activeFilters.value.find((f) => f.id === oldValue);

    if (filter?.originalOptions?.length > 0) {
      filter.options = [...filter.originalOptions];
    }
    if (
      filterInputRef &&
      typeof filterInputRef.submit === "function" &&
      filter
    ) {
      nextTick(() => {
        const result = filterInputRef.submit();

        // Although submit() emits 'submit'/'clear' events, when called programmatically
        // via component ref (not through template interaction), the @submit handler
        // in the template may not be triggered reliably. So we manually call the handler.
        //
        // undefined means "skip" - for field types like record that handle their own events
        if (result === undefined) {
          return;
        } else if (result && result.filterId) {
          handleFilterSubmit(result, filter);
        } else if (result === null) {
          handleFilterClear({ filterId: filter.id }, filter);
        }
      });
    }
  }
});

const fetchDynamicFilterOptions = async () => {
  if (!props.source && !props.isDynamicCRM) {
    return;
  }

  if (props.filtersOptions?.length > 0 && !props.isDynamicCRM) {
    return;
  }

  try {
    isLoadingFilters.value = true;

    if (props.isDynamicCRM && props.slug) {
      const result = await store.dispatch("schema/fetchSchema", {
        slug: props.slug,
        projectId: props.projectId || null,
        service: props.service || null,
      });

      if (result?.success && result?.schema?.attributes) {
        const nonRelationshipAttributes = result.schema.attributes.filter(
          (attr) => {
            const type = String(attr.type || "").toLowerCase();
            return (
              type !== "relationship" && type !== "file" && !attr.is_internal
            );
          },
        );

        const groupedAttributeSlug =
          props.currentView?.group_by_config?.attribute_slug;
        const groupedAttributeId =
          props.currentView?.group_by_config?.attribute_id;
        const isPipelineGroupBy = groupedAttributeSlug === "pipeline_stage_id";

        const isBoardView =
          props.currentView?.view_type === viewLayoutTypes.BOARD;
        let filteredAttributes =
          isBoardView && (groupedAttributeSlug || groupedAttributeId)
            ? nonRelationshipAttributes.filter(
                (attr) =>
                  attr.slug !== groupedAttributeSlug &&
                  attr.id !== groupedAttributeId,
              )
            : nonRelationshipAttributes;

        if (
          props.currentColumns &&
          props.currentColumns.length > 0 &&
          props.currentView?.view_type !== viewLayoutTypes.BOARD
        ) {
          filteredAttributes = filteredAttributes.filter((attr) =>
            props.currentColumns.includes(attr.slug),
          );
        }

        const staticFilterIds = new Set(props.filtersOptions.map((f) => f.id));
        const staticFilterAttributeIds = new Set(
          props.filtersOptions.map((f) => f.attribute_id).filter(Boolean),
        );
        const staticFilterSlugs = new Set(
          props.filtersOptions.flatMap((f) => {
            const variations = [f.id];

            if (f.id === "deal_stage")
              variations.push("stage", "deal_stage_id");
            if (f.id === "pipeline_id")
              variations.push("pipeline", "deal_pipeline");
            if (f.id === "pipeline_stage_id")
              variations.push("deal_stage", "stage", "pipeline_stage");
            if (f.attribute_id) variations.push(f.attribute_id);
            return variations;
          }),
        );

        if (staticFilterIds.size > 0) {
          filteredAttributes = filteredAttributes.filter((attr) => {
            return (
              !staticFilterIds.has(attr.slug) &&
              !staticFilterIds.has(attr.id) &&
              !staticFilterSlugs.has(attr.slug) &&
              !staticFilterSlugs.has(attr.id) &&
              !staticFilterAttributeIds.has(attr.id) &&
              !staticFilterAttributeIds.has(attr.slug)
            );
          });
        }

        const mappedAttributes = filteredAttributes.map((prop) => {
          const propType = toFilterType(prop.type);
          const propSlug = prop.slug || prop.name;
          const filterId = mapFilterId(propSlug);

          const propName = (prop.name || "").toLowerCase();
          const isRelationshipField =
            prop.type === "relationship" || prop.datatype === "relationship";
          const hasOptions = prop.options && prop.options.length > 0;
          const isStatusField = standardObjectStatusFields.includes(propSlug);
          const isCompanyField =
            propSlug.includes("company") || propName.includes("company");

          const isSearchable =
            isRelationshipField ||
            (propType === "select" && hasOptions) ||
            isStatusField ||
            isCompanyField;

          let normalizedOptions = [];
          if (prop.type === "boolean") {
            normalizedOptions = booleanOptions.map((opt) => ({
              id: opt.value,
              name: opt.label,
            }));
          } else {
            normalizedOptions = (prop.options || []).map((opt) => {
              if (typeof opt === "string") {
                return { id: opt, name: opt };
              }
              return {
                id: opt?.id ?? opt,
                name: opt?.name ?? opt?.id ?? opt,
              };
            });
          }

          return {
            id: filterId,
            attribute_id: prop.id,
            name: prop.name || prop.label,
            type: propType,
            datatype: prop.type,
            options: normalizedOptions,
            is_multi_value: prop.is_multi_value || false,
            searchable: isSearchable,
            related_objects: prop.related_objects || [],
            // Decorate the numeric id input with the schema's display
            // prefix (only when the schema actually has one).
            ...(propSlug === "id" && props.displayPrefix
              ? { displayPrefix: props.displayPrefix }
              : {}),
          };
        });

        dynamicFilterOptions.value = mappedAttributes;
      } else {
        dynamicFilterOptions.value = [];
      }
    } else {
      const entityType = getEntityTypeFromSource(props.source);

      const response = await DashboardService.fetchObjectProperties(entityType);

      if (response?.success && response?.entity_details) {
        const { properties, custom_properties } = response.entity_details;

        const apiProperties = [
          ...(properties || [])
            .filter(
              (prop) => String(prop.datatype || "").toLowerCase() !== "file",
            )
            .map((prop) => ({
              id: prop.name,
              name: prop.label,
              type: toFilterType(prop.datatype),
              datatype: prop.datatype,
              options: prop.options || [],
            })),

          ...(custom_properties || [])
            .filter(
              (prop) => String(prop.datatype || "").toLowerCase() !== "file",
            )
            .map((prop) => ({
              id: prop.slug || prop.name,
              name: prop.label || prop.name,
              type: toFilterType(prop.datatype),
              datatype: prop.datatype,
              options: prop.options || [],
            })),
        ];

        const filteredApiProperties = apiProperties.filter(
          (prop) => !["created_date", "last_activity"].includes(prop.id),
        );

        const allProperties = [...defaultDateFilters, ...filteredApiProperties];

        dynamicFilterOptions.value = allProperties;
      } else {
        dynamicFilterOptions.value = props.isDynamicCRM
          ? []
          : defaultFilterOptions;
      }
    }
  } catch (error) {
    console.error("Error fetching dynamic filter options:", error);
    dynamicFilterOptions.value = props.isDynamicCRM ? [] : defaultFilterOptions;
  } finally {
    isLoadingFilters.value = false;
  }
};

const initializeActiveFilters = async () => {
  if (!props.filterData || Object.keys(props.filterData).length === 0) {
    return;
  }

  if (props.showObjectSelector && dynamicFilterOptions.value.length === 0) {
    return;
  }

  if (dynamicFilterOptions.value.length === 0) {
    await fetchDynamicFilterOptions();
  }

  let availableOptions = [];
  const filteredStaticOptions = computedFiltersOptions.value;

  if (
    filteredStaticOptions.length > 0 &&
    dynamicFilterOptions.value.length > 0
  ) {
    // Create a map of static filters with their options
    const staticFiltersMap = new Map();
    filteredStaticOptions.forEach((f) => {
      staticFiltersMap.set(f.id, f);
      if (f.attribute_id) {
        staticFiltersMap.set(f.attribute_id, f);
      }
    });

    // Track which static filters have been used
    const usedStaticFilterIds = new Set();

    // Merge: prefer static filter if it has options loaded, otherwise use dynamic
    const mergedFilters = dynamicFilterOptions.value.map((dynamicFilter) => {
      // Check both id and attribute_id to find matching static filter
      const staticFilter =
        staticFiltersMap.get(dynamicFilter.id) ||
        staticFiltersMap.get(dynamicFilter.attribute_id);

      // If static filter exists and has options, use it instead of dynamic
      if (
        staticFilter &&
        staticFilter.options &&
        staticFilter.options.length > 0
      ) {
        usedStaticFilterIds.add(staticFilter.id);
        return staticFilter;
      }
      return dynamicFilter;
    });

    // Add static filters that weren't matched with any dynamic filter
    const uniqueStaticFilters = filteredStaticOptions.filter(
      (f) => !usedStaticFilterIds.has(f.id),
    );

    availableOptions = [...uniqueStaticFilters, ...mergedFilters];
  } else if (dynamicFilterOptions.value.length > 0) {
    availableOptions = dynamicFilterOptions.value;
  } else if (filteredStaticOptions.length > 0) {
    availableOptions = filteredStaticOptions;
  } else if (!props.isDynamicCRM) {
    availableOptions = defaultFilterOptions;
  }

  for (const [filterId, filterValue] of Object.entries(props.filterData)) {
    if (filterValue !== null && filterValue !== undefined) {
      let optionDef = availableOptions.find(
        (opt) =>
          opt.id === filterId ||
          opt.slug === filterId ||
          opt.attribute_id === filterId ||
          opt.name?.toLowerCase()?.replace(/\s+/g, "_") === filterId,
      );

      // If not found in availableOptions, check raw filtersOptions for locked filters
      // (locked filters are filtered out of computedFiltersOptions/availableOptions)
      if (!optionDef && props.lockedFilterKeys?.length > 0) {
        const isLockedFilter = props.lockedFilterKeys.some(
          (key) =>
            key === filterId ||
            key === filterId.toLowerCase().replace(/\s+/g, "_"),
        );
        if (isLockedFilter && props.filtersOptions?.length > 0) {
          optionDef = props.filtersOptions.find(
            (opt) =>
              opt.id === filterId ||
              opt.slug === filterId ||
              opt.attribute_id === filterId ||
              opt.name?.toLowerCase()?.replace(/\s+/g, "_") === filterId,
          );
        }
      }

      if (optionDef) {
        const hasStaticOptions =
          optionDef.options && optionDef.options.length > 0;

        const mappedOptions =
          optionDef.options?.map((opt) => ({
            id: typeof opt === "string" ? opt : opt.id || opt,
            name: typeof opt === "string" ? opt : opt.name || opt,
            selected: false,
            ...(typeof opt === "object" && opt?.isDeleted
              ? { isDeleted: true }
              : {}),
          })) || [];

        const newFilter = reactive({
          id: filterId,
          attribute_id: optionDef.attribute_id || filterId,
          name: optionDef.name || filterId,
          type: optionDef.type || getFilterTypeFromDatatype(optionDef.datatype),
          datatype: optionDef.datatype,
          multiSelect:
            optionDef.multiSelect !== undefined
              ? optionDef.multiSelect
              : isMultiSelect(optionDef),
          searchable:
            optionDef.searchable === true ||
            optionDef.type === "text" ||
            optionDef.type === "user" ||
            searchableFilterIds.includes(filterId),
          showInitials: optionDef.type === "user",
          options: mappedOptions,
          originalOptions: hasStaticOptions ? [...mappedOptions] : [],
          isLoading: false,
          related_objects: optionDef.related_objects || [],
          sortSelectedFirst: optionDef.sortSelectedFirst || false,
          fetchOptions: optionDef.fetchOptions || null,
          searchOptions: optionDef.searchOptions || null,
          dependsOn: optionDef.dependsOn || null,
        });

        applyFilterFlags(newFilter);

        if (!hasStaticOptions) {
          if (
            optionDef.fetchOptions &&
            typeof optionDef.fetchOptions === "function"
          ) {
            try {
              newFilter.isLoading = true;
              const options = await optionDef.fetchOptions(
                props.filterData,
                store,
              );
              newFilter.options = options || [];
              newFilter.originalOptions = options || [];
              newFilter.isLoading = false;
            } catch (error) {
              console.error("Error fetching filter options on init:", error);
              newFilter.isLoading = false;
            }
          } else {
            await fetchDynamicFilterData(newFilter);
          }
        }

        // Check for duplicates - match on id, attribute_id, or shared attribute_id
        const newAttrId = optionDef.attribute_id || filterId;
        const existingIndex = activeFilters.value.findIndex(
          (f) =>
            f.id === filterId ||
            f.attribute_id === filterId ||
            f.id === newAttrId ||
            f.attribute_id === newAttrId,
        );
        if (existingIndex === -1) {
          activeFilters.value.push(newFilter);
        }
      }
    }
  }
};

watch(
  () => props.source,
  (newSource) => {
    if (newSource) {
      fetchDynamicFilterOptions();
    }
  },
);

watch(
  () => props.slug,
  (newSlug) => {
    if (newSlug && props.isDynamicCRM) {
      fetchDynamicFilterOptions();
    }
  },
  { immediate: true },
);

watch(
  () => props.isDynamicCRM,
  (newValue) => {
    if (newValue && props.slug) {
      fetchDynamicFilterOptions();
    }
  },
);

watch(
  () => props.projectId,
  (newProjectId, oldProjectId) => {
    if (
      props.isDynamicCRM &&
      props.slug === entityTypes.TASKS &&
      newProjectId &&
      newProjectId !== oldProjectId
    ) {
      fetchDynamicFilterOptions();
    }
  },
);

watch(
  () => props.currentColumns,
  (newColumns, oldColumns) => {
    if (
      props.isDynamicCRM &&
      props.slug &&
      newColumns &&
      JSON.stringify(newColumns) !== JSON.stringify(oldColumns)
    ) {
      fetchDynamicFilterOptions();
    }
  },
  { deep: true },
);

watch(
  () => {
    const schemaKey =
      props.slug === entityTypes.TASKS && props.projectId
        ? `tasks_${props.projectId}`
        : props.slug;
    return store.state.schema?.schemas?.[schemaKey];
  },
  (newSchema, oldSchema) => {
    if (
      props.isDynamicCRM &&
      props.slug &&
      newSchema &&
      newSchema !== oldSchema
    ) {
      fetchDynamicFilterOptions();
    }
  },
  { deep: true },
);

watch(
  () => props.currentView?.group_by_config?.attribute_slug,
  (newAttributeSlug, oldAttributeSlug) => {
    if (
      props.isDynamicCRM &&
      props.slug &&
      newAttributeSlug !== oldAttributeSlug
    ) {
      fetchDynamicFilterOptions();
    }
  },
);

let isViewSwitching = ref(false);
let previousViewId = ref(null);

// Called by parent after filterData is updated with new view's filters
const reinitializeFilters = async () => {
  isViewSwitching.value = true;

  try {
    manuallyAddedFilterIds.value.clear();
    openDropdownId.value = null;
    activeFilters.value = [];
    filterInputRefs.value = {};

    await nextTick();

    if (props.isDynamicCRM) {
      await fetchDynamicFilterOptions();
    }

    if (props.filterData && Object.keys(props.filterData).length > 0) {
      await initializeActiveFilters();
    }
  } catch (error) {
    console.error("Error reinitializing filters:", error);
  } finally {
    isViewSwitching.value = false;
  }
};

watch(
  () => props.viewId,
  async (newViewId, oldViewId) => {
    if (newViewId && newViewId !== oldViewId && oldViewId !== undefined) {
      isViewSwitching.value = true;
      previousViewId.value = oldViewId;

      try {
        manuallyAddedFilterIds.value.clear();
        openDropdownId.value = null;
        activeFilters.value = [];
        filterInputRefs.value = {};

        await nextTick();
        await nextTick();

        if (props.isDynamicCRM) {
          await fetchDynamicFilterOptions();
        }

        if (props.filterData && Object.keys(props.filterData).length > 0) {
          await initializeActiveFilters();
        }
      } catch (error) {
        console.error("Error during view switch:", error);
      } finally {
        isViewSwitching.value = false;
      }
    }
  },
);

watch(
  () => props.filterData,
  async (newFilterData, oldFilterData) => {
    if (isViewSwitching.value) {
      return;
    }

    if (!newFilterData || Object.keys(newFilterData).length === 0) {
      activeFilters.value = activeFilters.value.filter((filter) =>
        manuallyAddedFilterIds.value.has(filter.id),
      );
      return;
    }

    if (activeFilters.value.length === 0) {
      // Don't initialize if waiting for object selection
      if (props.showObjectSelector && dynamicFilterOptions.value.length === 0) {
        return;
      }

      if (props.isDynamicCRM && dynamicFilterOptions.value.length === 0) {
        await fetchDynamicFilterOptions();
      }

      await initializeActiveFilters();
      return;
    }

    const newFilterKeys = Object.keys(newFilterData);

    const filterExistsInData = (filter) => {
      const idExists =
        newFilterKeys.includes(filter.id) &&
        newFilterData[filter.id] !== null &&
        newFilterData[filter.id] !== undefined;

      const attrIdExists =
        filter.attribute_id &&
        newFilterKeys.includes(filter.attribute_id) &&
        newFilterData[filter.attribute_id] !== null &&
        newFilterData[filter.attribute_id] !== undefined;

      return idExists || attrIdExists;
    };

    activeFilters.value = activeFilters.value.filter(
      (filter) =>
        manuallyAddedFilterIds.value.has(filter.id) ||
        filterExistsInData(filter),
    );

    for (const [filterId, filterValue] of Object.entries(newFilterData)) {
      if (filterValue !== null && filterValue !== undefined) {
        // First, build available options to find the optionDef
        // Use the same merge logic as initializeActiveFilters:
        // prefer static filters with loaded options over dynamic ones
        let availableOptions = [];
        const filteredStaticOptions = computedFiltersOptions.value;

        if (
          filteredStaticOptions.length > 0 &&
          dynamicFilterOptions.value.length > 0
        ) {
          const staticFiltersMap = new Map();
          filteredStaticOptions.forEach((f) => {
            staticFiltersMap.set(f.id, f);
            if (f.attribute_id) {
              staticFiltersMap.set(f.attribute_id, f);
            }
          });

          const usedStaticFilterIds = new Set();

          const mergedFilters = dynamicFilterOptions.value.map(
            (dynamicFilter) => {
              const staticFilter =
                staticFiltersMap.get(dynamicFilter.id) ||
                staticFiltersMap.get(dynamicFilter.attribute_id);
              if (staticFilter) {
                usedStaticFilterIds.add(staticFilter.id);
                return staticFilter;
              }
              return dynamicFilter;
            },
          );

          const uniqueStaticFilters = filteredStaticOptions.filter(
            (f) => !usedStaticFilterIds.has(f.id),
          );

          availableOptions = [...uniqueStaticFilters, ...mergedFilters];
        } else if (dynamicFilterOptions.value.length > 0) {
          availableOptions = dynamicFilterOptions.value;
        } else if (filteredStaticOptions.length > 0) {
          availableOptions = filteredStaticOptions;
        } else if (!props.isDynamicCRM) {
          availableOptions = defaultFilterOptions;
        }

        // Find the option definition for this filterId
        let optionDef = availableOptions.find(
          (opt) =>
            opt.id === filterId ||
            opt.slug === filterId ||
            opt.attribute_id === filterId ||
            opt.name?.toLowerCase()?.replace(/\s+/g, "_") === filterId,
        );

        // If not found in availableOptions, check raw filtersOptions for locked filters
        // (locked filters are filtered out of computedFiltersOptions/availableOptions)
        if (!optionDef && props.lockedFilterKeys?.length > 0) {
          const isLockedFilter = props.lockedFilterKeys.some(
            (key) =>
              key === filterId ||
              key === filterId.toLowerCase().replace(/\s+/g, "_"),
          );
          if (isLockedFilter && props.filtersOptions?.length > 0) {
            optionDef = props.filtersOptions.find(
              (opt) =>
                opt.id === filterId ||
                opt.slug === filterId ||
                opt.attribute_id === filterId ||
                opt.name?.toLowerCase()?.replace(/\s+/g, "_") === filterId,
            );
          }
        }

        // Now check for existing filter using BOTH filterId AND optionDef's properties
        const existingFilter = activeFilters.value.find((f) => {
          // Direct match on filterId
          if (f.id === filterId || f.attribute_id === filterId) return true;
          // If we found an optionDef, also check if any filter matches its id or attribute_id
          if (optionDef) {
            if (f.id === optionDef.id || f.attribute_id === optionDef.id)
              return true;
            if (
              f.id === optionDef.attribute_id ||
              f.attribute_id === optionDef.attribute_id
            )
              return true;
          }
          return false;
        });

        if (!existingFilter && optionDef) {
          const hasStaticOptions =
            optionDef.options && optionDef.options.length > 0;

          const mappedOptions =
            optionDef.options?.map((opt) => ({
              id: typeof opt === "string" ? opt : opt.id || opt,
              name: typeof opt === "string" ? opt : opt.name || opt,
              selected: false,
              ...(typeof opt === "object" && opt?.isDeleted
                ? { isDeleted: true }
                : {}),
            })) || [];

          const newFilter = reactive({
            id: filterId,
            attribute_id: optionDef.attribute_id || filterId,
            name: optionDef.name || filterId,
            type:
              optionDef.type || getFilterTypeFromDatatype(optionDef.datatype),
            datatype: optionDef.datatype,
            multiSelect:
              optionDef.multiSelect !== undefined
                ? optionDef.multiSelect
                : isMultiSelect(optionDef),
            searchable:
              optionDef.searchable === true ||
              optionDef.type === "text" ||
              optionDef.type === "user" ||
              searchableFilterIds.includes(filterId),
            showInitials: optionDef.type === "user",
            options: mappedOptions,
            originalOptions: hasStaticOptions ? [...mappedOptions] : [],
            isLoading: false,
            related_objects: optionDef.related_objects || [],
            sortSelectedFirst: optionDef.sortSelectedFirst || false,
            fetchOptions: optionDef.fetchOptions || null,
            searchOptions: optionDef.searchOptions || null,
            dependsOn: optionDef.dependsOn || null,
          });

          // Apply field-specific flags
          applyFilterFlags(newFilter);

          if (!hasStaticOptions) {
            if (typeof optionDef.fetchOptions === "function") {
              try {
                newFilter.isLoading = true;
                const opts = await optionDef.fetchOptions(
                  props.filterData,
                  store,
                );
                if (opts && opts.length > 0) {
                  newFilter.options = opts;
                  newFilter.originalOptions = [...opts];
                }
                newFilter.isLoading = false;
              } catch (e) {
                console.error("Error fetching filter options:", e);
                newFilter.isLoading = false;
              }
            } else {
              await fetchDynamicFilterData(newFilter);
            }
          }

          activeFilters.value.push(newFilter);
        }
      }
    }
  },
  { deep: true, immediate: true },
);

watch(
  () => availableObjects.value,
  async (objects) => {
    if (
      props.showObjectSelector &&
      objects?.length > 0 &&
      !selectedObjectId.value
    ) {
      const objectWithFilters = findObjectWithFilterProperties(
        objects,
        props.filterData,
        props.rawFilterKeys,
      );
      const targetObject = objectWithFilters || objects[0];

      await handleObjectChange(targetObject.id);
      await nextTick();
      if (props.filterData && Object.keys(props.filterData).length > 0) {
        await initializeActiveFilters();
      }
    }
  },
  { immediate: true },
);

watch(
  () => props.filterData,
  async (newFilterData) => {
    for (const filter of activeFilters.value) {
      if (filter.dependsOn && filter.fetchOptions) {
        const hasChangedDependency = filter.dependsOn.some(
          (depId) => depId in newFilterData,
        );

        if (hasChangedDependency) {
          try {
            filter.isLoading = true;
            const options = await filter.fetchOptions(newFilterData, store);
            filter.options = options || [];
            filter.originalOptions = options || [];
            filter.isLoading = false;

            // Drop selected values that are no longer in the new options.
            // This prevents showing raw IDs when a dependency (e.g. pipeline)
            // changes and the dependent filter (e.g. deal_stage) has stale
            // selections that haven't been cleaned up by the parent yet.
            const currentValue =
              newFilterData[filter.attribute_id] ?? newFilterData[filter.id];
            if (currentValue !== null && currentValue !== undefined) {
              const validIds = new Set(
                (options || []).map((o) => String(o.id)),
              );
              let selectedIds = [];
              if (Array.isArray(currentValue)) {
                selectedIds = currentValue;
              } else if (
                currentValue &&
                typeof currentValue === "object" &&
                Array.isArray(currentValue.value)
              ) {
                selectedIds = currentValue.value;
              }
              const remaining = selectedIds.filter((id) =>
                validIds.has(String(id)),
              );
              if (remaining.length !== selectedIds.length) {
                emit("filter-changed", {
                  filter: filter.attribute_id || filter.id,
                  updated_values:
                    remaining.length > 0
                      ? Array.isArray(currentValue)
                        ? remaining
                        : { ...currentValue, value: remaining }
                      : null,
                });
              }
            }
          } catch (error) {
            console.error(
              `Error refreshing dependent filter ${filter.id}:`,
              error,
            );
            filter.isLoading = false;
          }
        }
      }
    }
  },
  { deep: true },
);

onMounted(async () => {
  document.addEventListener("click", handleOutsideClick);

  store.dispatch("users/fetchUsersWithDeleted");

  if (!props.isDynamicCRM || !props.slug) {
    await fetchDynamicFilterOptions();
  }
  await initializeActiveFilters();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});

const dynamicCRMDateFilterOptions = [
  { id: "today", name: "Today" },
  { id: "yesterday", name: "Yesterday" },
  { id: "last_3_days", name: "Last 3 days" },
  { id: "last_7_days", name: "Last 7 days" },
  { id: "last_30_days", name: "Last 30 days" },
  { id: "this_month", name: "This month" },
  { id: "last_month", name: "Last month" },
  { id: "next_7_days", name: "Next 7 days" },
  { id: "custom", name: "Custom" },
];

const fetchDynamicFilterData = async (filter) => {
  try {
    const ctx = {
      isDynamicCRM: props.isDynamicCRM,
      slug: props.slug,
      source: props.source,
      entityType: props.slug || props.source,
    };

    const isDateFilter =
      filter.type === "date" ||
      filter.datatype === "Date" ||
      filter.datatype === "date" ||
      dateFilterTypes.includes(filter.id);

    // Use custom date filter options for DynamicCRM date filters
    if (props.isDynamicCRM && isDateFilter) {
      filter.options = dynamicCRMDateFilterOptions.map((opt) => ({
        ...opt,
        selected: false,
      }));
      filter.originalOptions = [...filter.options];
      return;
    }

    const options = await loadFilterOptions(filter, ctx);

    if (options && options.length > 0) {
      filter.options = options;
      if (!filter.originalOptions || filter.originalOptions.length === 0) {
        filter.originalOptions = [...options];
      }
    } else {
      applyFilterFlags(filter);
      filter.options = [];
      filter.originalOptions = [];
    }
  } catch (error) {
    console.error(
      `Error fetching dynamic data for filter ${filter.name}:`,
      error,
    );
  } finally {
    filter.isLoading = false;
  }
};

const runFilterSearchOptions = debounce(async (filter, query) => {
  try {
    filter.isLoading = true;
    const opts = await filter.searchOptions(query);
    filter.options = opts || [];
    if (!query) {
      filter.originalOptions = [...filter.options];
    }
  } catch (error) {
    console.error(
      `Error searching options for filter ${filter.name}:`,
      error,
    );
    filter.options = [];
  } finally {
    filter.isLoading = false;
  }
}, 300);

const handleFilterSearch = async (filterId, searchQuery, filter) => {
  if (!filter) return;

  const lowerSearchQuery = (searchQuery || "").toLowerCase().trim();

  if (typeof filter.searchOptions === "function") {
    runFilterSearchOptions(filter, lowerSearchQuery);
    emit("filter-search", filterId, searchQuery);
    return;
  }

  if (!lowerSearchQuery) {
    if (filter.originalOptions && filter.originalOptions.length > 0) {
      filter.options = [...filter.originalOptions];
    }
    emit("filter-search", filterId, searchQuery);
    return;
  }

  const isCompanyFilter =
    filterId === "company" ||
    filterId.includes("current_company") ||
    filterId.includes("deal_company") ||
    (filter.name || "").toLowerCase().includes("company") ||
    (filter.name || "").toLowerCase().includes("current_company") ||
    (filter.name || "").toLowerCase().includes("deal_company");

  const isIndustryFilter =
    (filterId.includes("industry") ||
      (filter.name || "").toLowerCase().includes("industry")) &&
    (props.slug === entityTypes.COMPANIES ||
      props.source === entityTypes.COMPANY);

  if (isCompanyFilter || isIndustryFilter) {
    try {
      filter.isLoading = true;

      const options = await loadFilterOptions(filter, {
        isDynamicCRM: props.isDynamicCRM,
        slug: props.slug,
        source: props.source,
        searchQuery: lowerSearchQuery,
        entityType: props.slug || props.source,
      });

      if (options && options.length > 0) {
        filter.options = options;
      } else {
        filter.options = [];
      }
    } catch (error) {
      console.error(
        `Error searching options for filter ${filter.name}:`,
        error,
      );
      filter.options = [];
    } finally {
      filter.isLoading = false;
    }
  } else {
    if (filter.originalOptions && filter.originalOptions.length > 0) {
      filter.options = filter.originalOptions.filter((opt) =>
        (opt.name || "").toLowerCase().includes(lowerSearchQuery),
      );
    }
  }

  emit("filter-search", filterId, searchQuery);
};

defineExpose({
  fetchDynamicFilterOptions,
  reinitializeFilters,
  resetInternalState,
});

const getCurrentSortOptions = () => {
  if (props.sortData && Object.keys(props.sortData).length > 0) {
    return props.sortData;
  }

  return {};
};

const handleOpenExportModal = async () => {
  if (hasNoResults.value) {
    return;
  }
  showExportModal.value = true;
};

const handleExportConfirm = async (exportData) => {
  try {
    const sortOptions = getCurrentSortOptions();
    const browserTimezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    const params = new URLSearchParams({
      object_type: props.slug || props.source,
      export_email: exportData.email,
      browser_timezone: browserTimezone,
    });

    if (props.currentView?.id) {
      params.append("view_id", props.currentView.id);
    }

    if (props.currentView?.name) {
      params.append("view_name", props.currentView.name);
    }

    if (Object.keys(props.filterData || {}).length > 0) {
      params.append("filters", JSON.stringify(props.filterData));
    }

    if (Object.keys(sortOptions).length > 0) {
      params.append("options", JSON.stringify({ sort: sortOptions }));
    }

    if (props.searchQuery && props.searchQuery.trim() !== "") {
      params.append("search_query", props.searchQuery);
    }

    if (props.projectId) {
      params.append("project_id", props.projectId);
    }

    let response;
    if (props.isDynamicCRM && props.slug === entityTypes.TICKETS) {
      response = await TicketsService.exportCSV(Object.fromEntries(params));
    } else if (props.slug === "tasks") {
      response = await DashboardService.exportTasks(Object.fromEntries(params));
    } else if (props.source === entityTypes.CALL_LOG) {
      response = await CallService.exportDialerCallLogs(
        Object.fromEntries(params),
      );
    } else if (props.source === entityTypes.VOICE_AGENT_CALL_LOG) {
      const vaParams = Object.fromEntries(params);
      vaParams.filters = JSON.stringify(
        buildFiltersArray(props.filterData || {}),
      );
      response = await VoiceAgentService.exportVoiceAgentCallLogs(vaParams);
    } else {
      response = await DashboardService.exportCrmV2Data(params);
    }

    if (response?.success) {
      showExportModal.value = false;
      showExportSuccessMessage();
    } else {
      message.error(
        response?.errors ||
          response?.message ||
          "Export failed. Please try again.",
      );
    }
  } catch (error) {
    message.error("Export failed. Please try again.");
  }
};

const showExportSuccessMessage = () => {
  const viewName =
    props.currentView?.name || `All ${props.slug || props.source}`;

  showDynamicToast({
    type: "success",
    text: `${viewName} will be exported. You can view your exports here`,
    actionFunction: () => {
      window.location.href = "/settings#exports";
    },
    actionText: "View Exports",
    duration: 8,
  });
};

const handleViewCreated = () => {
  emit("view-created");
};

const handleViewUpdated = (eventData) => {
  if (
    eventData?.view?.view_type &&
    props.currentView?.view_type &&
    eventData.view.view_type !== props.currentView.view_type
  ) {
    emit("clear-all-filters", {});
  }
  emit("view-updated", eventData);
};
</script>
